/**
 * Graph rendering functionality
 * Draws and animates a curve from "Today" to "Significant results"
 */

/**
 * Format date as "Mon DD" (e.g. "Nov 10")
 */
function formatDate(date) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    return `${month} ${day}`;
}

/**
 * Get date label from days offset
 * @param {number} daysFromToday - Number of days from today (can be negative, 0, or positive)
 * @returns {string} Formatted date label (e.g. "Nov 10")
 */
function getDateLabel(daysFromToday) {
    const today = new Date();
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysFromToday);
    return formatDate(targetDate);
}

export function renderMilestoneChart(opts = {}) {
    const {
        containerId = "graph",
        leftDateLabel,
        rightDateLabel,
        leftDaysFromToday = 0,
        rightDaysFromToday = 42,
        todayLabel = "Today",
        endLabel = "Significant<br>results",
        width = 340,
        height = 300,
        durationMs = 1600,
        easing = t => 1 - Math.pow(1 - t, 3), // easeOutCubic
        startColor = "#FFA230",
        endColor = "#A146B0"
    } = opts;

    // Use provided labels or calculate from days
    const leftLabel = leftDateLabel || getDateLabel(leftDaysFromToday);
    const rightLabel = rightDateLabel || getDateLabel(rightDaysFromToday);

    const NS = "http://www.w3.org/2000/svg";
    const XHTML = "http://www.w3.org/1999/xhtml";
    const xStart = 40, yStart = 210;
    const xEnd = 300, yEnd = 60;

    const container = document.getElementById(containerId);
    if (!container) return;
    container.innerHTML = "";

    const svg = document.createElementNS(NS, "svg");
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg.setAttribute("width", "100%");

    // Grid lines
    [260, 210, 160, 110, 60].forEach(y => {
        const l = document.createElementNS(NS, "line");
        l.setAttribute("x1", 0);
        l.setAttribute("y1", y);
        l.setAttribute("x2", width);
        l.setAttribute("y2", y);
        l.setAttribute("stroke", "rgba(255,255,255,1.0)");
        l.setAttribute("stroke-width", "2");
        svg.appendChild(l);
    });

    // Dashed guidelines
    const dash = (x, y1, y2, color = "rgba(255,255,255,0.25)") => {
        const p = document.createElementNS(NS, "path");
        p.setAttribute("d", `M ${x} ${y1} L ${x} ${y2}`);
        p.setAttribute("stroke", color);
        p.setAttribute("stroke-width", "3");
        p.setAttribute("opacity", "0.5");
        p.setAttribute("stroke-dasharray", "8 6");
        p.setAttribute("fill", "none");
        svg.appendChild(p);
        return p;
    };

    dash(xStart, height - 42, yStart, startColor);
    const endDash = dash(xEnd, height - 42, yEnd, endColor);
    endDash.style.opacity = "0";

    // Date labels
    const dateLabel = (x, txt, color) => {
        const fo = document.createElementNS(NS, "foreignObject");
        fo.setAttribute("x", x - 24);
        fo.setAttribute("y", height - 25);
        fo.setAttribute("width", 120);
        fo.setAttribute("height", 30);
        const div = document.createElementNS(XHTML, "div");
        div.textContent = txt;
        div.style.color = color;
        div.style.font = "12px/1.0 Montserrat, system-ui, -apple-system";
        div.style.fontWeight = "600";
        fo.appendChild(div);
        svg.appendChild(fo);
        return fo;
    };

    dateLabel(xStart, leftLabel, startColor);
    const endDateLabel = dateLabel(xEnd, rightLabel, endColor);
    endDateLabel.style.opacity = "0";

    // Bubble labels
    const bubbleLabel = (x, y, txt, bg) => {
        const fo = document.createElementNS(NS, "foreignObject");
        fo.setAttribute("x", x);
        fo.setAttribute("y", y);
        fo.setAttribute("width", 220);
        fo.setAttribute("height", 50);

        const div = document.createElementNS(XHTML, "div");
        div.innerHTML = txt;
        div.style.display = "inline-block";
        div.style.background = bg;
        div.style.color = "#fff";
        div.style.font = "12px/1.0 Montserrat, system-ui, -apple-system";
        div.style.fontWeight = "700";
        div.style.padding = "5px 14px";
        div.style.borderRadius = "8px";
        div.style.boxShadow = "0 6px 20px rgba(0,0,0,0.25)";
        div.style.whiteSpace = "nowrap";
        div.style.margin = "0 auto";
        div.style.textAlign = "center";

        fo.appendChild(div);
        svg.appendChild(fo);
        return fo;
    };

    bubbleLabel(xStart - 40, yStart - 42, todayLabel, startColor);
    const endBubbleLabel = bubbleLabel(xEnd - 85, yEnd - 50, endLabel, endColor);
    endBubbleLabel.style.opacity = "0";

    // Gradient
    const defs = document.createElementNS(NS, "defs");
    const grad = document.createElementNS(NS, "linearGradient");
    grad.setAttribute("id", `line_chart_gradient_${containerId}`);
    grad.setAttribute("x1", xStart);
    grad.setAttribute("y1", yStart);
    grad.setAttribute("x2", xEnd);
    grad.setAttribute("y2", yEnd);
    grad.setAttribute("gradientUnits", "userSpaceOnUse");

    const s1 = document.createElementNS(NS, "stop");
    s1.setAttribute("offset", "0");
    s1.setAttribute("stop-color", startColor);

    const s2 = document.createElementNS(NS, "stop");
    s2.setAttribute("offset", "1");
    s2.setAttribute("stop-color", endColor);

    grad.appendChild(s1);
    grad.appendChild(s2);
    defs.appendChild(grad);
    svg.appendChild(defs);

    // Curve path
    const d = `M ${xStart} ${yStart} 
   C 100 186 90 160 129 149 
     C 150 143 171 154 198 138 
     C 227 120 206 108 242 90 
     C 270 80 280 94 ${xEnd} ${yEnd}`;

    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", d);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", `url(#line_chart_gradient_${containerId})`);
    path.setAttribute("stroke-width", "5.5");
    path.setAttribute("stroke-linecap", "round");
    path.style.strokeDasharray = "0 1";
    path.style.strokeDashoffset = "0";
    svg.appendChild(path);

    // Start dot
    const startDot = document.createElementNS(NS, "circle");
    startDot.setAttribute("r", 7);
    startDot.setAttribute("cx", xStart);
    startDot.setAttribute("cy", yStart);
    startDot.setAttribute("fill", startColor);
    startDot.setAttribute("stroke", "white");
    startDot.setAttribute("stroke-width", "2");
    startDot.style.filter = "drop-shadow(0 0 6px rgba(0,180,255,0.6))";
    svg.appendChild(startDot);

    // Moving marker
    const mover = document.createElementNS(NS, "circle");
    mover.setAttribute("r", 7);
    mover.setAttribute("fill", endColor);
    mover.setAttribute("stroke", "white");
    mover.setAttribute("stroke-width", "2");
    mover.style.filter = "drop-shadow(0 0 6px rgba(0,180,255,0.6))";
    mover.style.opacity = "0";
    svg.appendChild(mover);

    container.appendChild(svg);

    // Animate
    const total = path.getTotalLength();
    path.style.strokeDasharray = `0 ${total}`;
    path.style.strokeDashoffset = "0";

    const t0 = performance.now();

    function frame(now) {
        const raw = Math.min(1, (now - t0) / durationMs);
        const t = easing(raw);
        const visible = total * t;

        path.style.strokeDasharray = `${visible} ${total - visible}`;

        const pt = path.getPointAtLength(Math.max(0.0001, visible));
        mover.setAttribute("cx", pt.x);
        mover.setAttribute("cy", pt.y);

        if (raw < 1) {
            requestAnimationFrame(frame);
        } else {
            endDash.style.transition = "opacity 0ms ease";
            endDash.style.opacity = "0.5";
            endDateLabel.style.transition = "opacity 0ms ease";
            endDateLabel.style.opacity = "1";
            mover.style.opacity = "1";
            mover.style.transition = "opacity 0ms ease";
            endBubbleLabel.style.transition = "opacity 0ms ease";
            endBubbleLabel.style.opacity = "1";
        }
    }

    requestAnimationFrame(frame);
}

